/**
 * Which agent runtimes the viewer can run right now — the frontend half of
 * the backend's "can this user run the agent" rule (session create's
 * pre-flight and the job routes' ``no_ai_account`` gate): an active
 * personal link on a provider the server registers, OR that provider's
 * deployment-wide central billing.
 *
 * Shared by the setup checklist's "credential" step and the job entry
 * points (brand composer, prompt-fragment composer) so they can't
 * disagree about whether a run will start.
 */
import {
  useAgentProviders,
  useAnthropicAccounts,
  useAnthropicCentralBilling,
  useGithubAccounts,
  useGithubCentralBilling,
  useOpenAIAccounts,
  useServerBilling,
} from "@/api/hooks";

const hasActive = (rows: { status: string }[] | undefined): boolean =>
  Array.isArray(rows) && rows.some((a) => a.status === "active");

/** OpenAI's resolver tries the user's PREFERRED account first and, when it
 *  is in ``needs_refresh``, refreshes it inline before the run — so that
 *  account still counts. Any other account must be ``active``. */
const hasUsableOpenAI = (
  rows: { status: string; preferred?: boolean }[] | undefined,
): boolean =>
  hasActive(rows) ||
  (Array.isArray(rows) &&
    rows.some((a) => a.preferred === true && a.status === "needs_refresh"));

export type ProviderCredentials = {
  /** True once every query behind the answer has settled. */
  ready: boolean;
  /** Whether the viewer can run ``slug`` (false for an unknown slug). */
  usable: (slug: string) => boolean;
  /** Registered runtimes the viewer can run, the server's default first. */
  usableSlugs: string[];
  /** Whether the viewer can run any registered runtime. */
  any: boolean;
};

export function useProviderCredentials(): ProviderCredentials {
  const providers = useAgentProviders();
  const openai = useOpenAIAccounts();
  const anthropic = useAnthropicAccounts();
  const github = useGithubAccounts();
  const serverBilling = useServerBilling();
  const anthropicBilling = useAnthropicCentralBilling();
  const githubBilling = useGithubCentralBilling();

  // Only a credential for a runtime the server REGISTERS counts: an active
  // Claude link on a deployment without Claude Code can't run anything.
  // Unknown list (still loading) → count everything, so a caller never
  // flashes a false "not usable".
  const providerList = Array.isArray(providers.data) ? providers.data : null;
  const registered = providerList?.map((p) => p.slug) ?? null;
  const offers = (slug: string) => registered === null || registered.includes(slug);

  const credentialFor: Record<string, boolean> = {
    codex:
      hasUsableOpenAI(openai.data) ||
      (serverBilling.data?.server_billing_available ?? false),
    claude_code:
      hasActive(anthropic.data) ||
      (anthropicBilling.data?.central_billing ?? false),
    copilot:
      hasActive(github.data) ||
      (githubBilling.data?.central_billing ?? false),
  };
  const usable = (slug: string): boolean =>
    offers(slug) && (credentialFor[slug] ?? false);

  const ordered = [...(providerList ?? [])].sort(
    (a, b) => Number(Boolean(b.default)) - Number(Boolean(a.default)),
  );
  const usableSlugs = ordered.map((p) => p.slug).filter(usable);
  const any = Object.keys(credentialFor).some(usable);

  const ready =
    !providers.isLoading &&
    !openai.isLoading &&
    !anthropic.isLoading &&
    !github.isLoading &&
    !serverBilling.isLoading &&
    !anthropicBilling.isLoading &&
    !githubBilling.isLoading;

  return { ready, usable, usableSlugs, any };
}
